import React, { useState } from 'react';
import { Button } from '@fiverr-private/button';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@fiverr-private/overlay';
import { Typography } from '@fiverr-private/typography';
import pathfinder from '@fiverr-private/pathfinder';
import { logger } from '@fiverr-private/obs';
import { getContext } from '@fiverr-private/fiverr_context';
import type { StepSlug } from '@fiverr-private/store_builder_types';
import { Stack } from '@fiverr-private/layout_components';
import { AppI18n } from '../../i18n';
import { useDeleteStoreData } from '../../mutations/useDeleteStoreData';
import { useStoreData } from '../../hooks/useStoreData';

interface ResetStoreModalProps {
    shouldOpen: boolean;
}

const DEFAULT_NICHE_ID = '2';

export const ResetStoreModal = ({ shouldOpen }: ResetStoreModalProps) => {
    const { pageCtxId, queryParameters } = getContext();
    const { storeData } = useStoreData();

    const { mutateAsync: deleteStoreDataMutation } = useDeleteStoreData();
    const [isOpen, setIsOpen] = useState(shouldOpen);

    const close = () => setIsOpen(false);

    const onCreateNewStore = async () => {
        const niche = queryParameters?.niche ? String(queryParameters.niche) : DEFAULT_NICHE_ID;
        if (!storeData || !niche) {
            return;
        }

        const stepSlug: StepSlug = 'choose-banners';

        try {
            await deleteStoreDataMutation();

            const path = pathfinder(
                'ai_store_builder_wizard',
                {
                    platform: 'shopify',
                    step_slug: stepSlug,
                },
                {
                    query: {
                        niche,
                    },
                }
            );

            window.location.href = path;
        } catch (error) {
            logger.error(error as Error, {
                describe: 'Cannot delete store data',
                store_id: storeData.id,
                user_id: storeData.userId,
                page_ctx_id: pageCtxId,
            });
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={close} mobileFullScreen={false}>
            <ModalContent>
                <ModalHeader>
                    <AppI18n k="reset_store_modal.title" />
                </ModalHeader>
                <ModalBody>
                    <Typography>
                        <AppI18n k="reset_store_modal.description" />
                    </Typography>
                </ModalBody>
                <ModalFooter>
                    <Stack direction={{ default: 'column', md: 'row' }} gap="3">
                        <Button onClick={onCreateNewStore} variant="ghost">
                            <AppI18n k="reset_store_modal.create_new_store" />
                        </Button>
                        <Button onClick={close}>
                            <AppI18n k="reset_store_modal.complete_store_setup" />
                        </Button>
                    </Stack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

ResetStoreModal.displayName = 'ResetStoreModal';
